import { OryNetworkError } from '@shopware/iam-ory';
import { MonoTypeOperatorFunction, retry, throwError, timer } from 'rxjs';

/**
 * RxJS operator for retrying failed operations with exponential backoff.
 *
 * @remarks
 * **Use Case:**
 * Handles transient network errors and rate limiting (HTTP 429) from Ory APIs.
 * On Ory Network, rate limiting appears as `OryNetworkError` due to CORS blocking.
 *
 * **Retry Strategy:**
 * - Only retries retryable `OryNetworkError` instances (network failures, timeouts, rate limits)
 * - Skips cancelled requests (`isRequestAborted`) — not a transient failure
 * - Uses exponential backoff: initialDelay × 2^(retryCount-1), capped at maxDelay
 * - Default: 5s, 10s, 20s, 40s, 60s (5 attempts, ~135 seconds total)
 * - Resets retry count after successful request
 * - Does NOT retry validation errors, flow expired, or other non-network errors
 *
 * **Safety:**
 * Safe for idempotent operations (GET requests, code generation, resend operations).
 * Do NOT use for non-idempotent operations without careful consideration.
 *
 * @param config - Optional configuration for retry behavior
 *
 * @example
 * ```typescript
 * // Default configuration (5 retries, 5s initial, 60s max)
 * return this.oryService.executeFlow('verification', flow, data).pipe(
 *   retryWithExponentialBackoff()
 * );
 *
 * // Custom configuration (3 retries, 2s initial, 30s max)
 * return this.oryService.getFlow('settings').pipe(
 *   retryWithExponentialBackoff({ count: 3, initialDelaySeconds: 2, maxDelaySeconds: 30 })
 * );
 * ```
 */
export function retryWithExponentialBackoff<T>(config?: {
  count?: number;
  initialDelaySeconds?: number;
  maxDelaySeconds?: number;
}): MonoTypeOperatorFunction<T> {
  const {
    count = 5,
    initialDelaySeconds = 5,
    maxDelaySeconds = 60
  } = config ?? {};

  return retry({
    count,
    delay: (error, retryCount) => {
      // Only retry retryable network errors (includes rate limiting on Ory Network)
      // Cancelled requests (ERR_CANCELED) are not retryable — the operation was
      // intentionally aborted (e.g. navigation), not a transient failure.
      if (error instanceof OryNetworkError && !error.isRequestAborted) {
        // Exponential backoff with cap
        const delaySeconds = Math.min(
          initialDelaySeconds * Math.pow(2, retryCount - 1),
          maxDelaySeconds
        );
        const delayMs = delaySeconds * 1000;

        return timer(delayMs);
      }

      // Don't retry other errors (validation, flow expired, etc.) - rethrow immediately
      return throwError(() => error);
    },
    resetOnSuccess: true // Reset retry count after successful request
  });
}
